import { memo } from 'react';

import { DesignAvatar } from '@components/design-avatar';
import { IconTemplate } from '@components/icons/template';
import { Menu } from '@components/menu';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';

import { useConvertJourneyActionToAIAgentTemplate } from 'api/journey-actions/hooks';
import { JourneyAction } from 'api/types';
import { ACTION_TYPES, getActionLabel } from 'ui/actions/action-config';
import { NodeActions } from 'ui/journey-builder/node-types/common';
import { JourneyNodeActionCard, NodeContainer } from 'ui/journey-builder/node-types/node-container';
import { JourneyNode } from 'ui/journey-builder/node-types/types';

const ACTION_TYPE = ACTION_TYPES.ask_ai_agent;

export const getDescription = (action: JourneyAction<typeof ACTION_TYPE>) => {
  const { t } = getScopedTranslate(nodeLocale);
  return action.description ?? t('noDescription');
};

export const Node = memo((node: JourneyNode<typeof ACTION_TYPE>) => {
  const { t } = useScopedTranslate(nodeLocale);
  const convertMutation = useConvertJourneyActionToAIAgentTemplate();
  const isJourneyAIAgent = node.data.values?.aiAgentSource === 'action';

  return (
    <NodeContainer node={node}>
      <JourneyNodeActionCard
        isColorful
        node={node}
        label={node.data.values?.name ?? getActionLabel(ACTION_TYPE)}
        descriptionText={node.data.description}
        icon={<DesignAvatar src={node.data.values?.picture} />}
      >
        <NodeActions
          node={node}
          customMenuItems={
            isJourneyAIAgent ? (
              <Menu.Item
                label={t('convertToTemplate')}
                icon={<IconTemplate />}
                onSelect={() => convertMutation.mutate(node.id)}
                isDisabled={convertMutation.isLoading}
              />
            ) : undefined
          }
        />
      </JourneyNodeActionCard>
    </NodeContainer>
  );
});

const nodeLocale = {
  en: {
    convertToTemplate: 'Convert to template',
    noDescription: 'No description',
  },
};
